import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useNavigate, Link, Navigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { supabase } from "@/integrations/supabase/client";
import { S1Logo } from "@/components/S1Logo";
import { useToast } from "@/hooks/use-toast";
import { CheckEmailScreen } from "@/components/auth/CheckEmailScreen";
import { MapPin, Eye, EyeOff } from "lucide-react";

const Login = () => {
  const [isSignup, setIsSignup] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [city, setCity] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState<string | null>(null);
  const navigate = useNavigate();
  const { signIn, signUp, user, loading: authLoading } = useAuth();
  const { toast } = useToast();

  // Rediriger si deja authentifie avec email confirme
  if (!authLoading && user?.email_confirmed_at) {
    return <Navigate to="/" replace />;
  }

  // Inscription réussie : écran plein écran « vérifie ta boîte mail »
  if (submittedEmail) {
    return (
      <CheckEmailScreen
        email={submittedEmail}
        onBack={() => {
          setSubmittedEmail(null);
          setIsSignup(false);
        }}
      />
    );
  }

  const handleOAuth = async (provider: "google" | "linkedin_oidc") => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider,
      options: { redirectTo: `${window.location.origin}/auth/callback` },
    });
    if (error) {
      toast({ title: "Erreur de connexion", description: error.message, variant: "destructive" });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isSignup) {
        const { error } = await signUp(email, password, firstName, lastName, city);
        if (error) throw error;
        setSubmittedEmail(email);
      } else {
        const { error } = await signIn(email, password);
        if (error) throw error;
        navigate("/");
      }
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : "Une erreur est survenue";
      toast({ title: "Erreur", description: msg, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-[100dvh] items-center justify-center bg-background p-4">
      <h1 className="sr-only">Connexion</h1>
      <div className="w-full max-w-sm space-y-4">
        <Card className="bg-card border-border">
          <CardHeader className="text-center space-y-3">
            <div className="flex justify-center">
              <S1Logo variant="full" height={56} />
            </div>
            <CardTitle className="text-xl">
              {isSignup ? "Créer un compte" : "Connexion à Systeme1-immo"}
            </CardTitle>
            <p className="text-xs text-muted-foreground">
              {isSignup
                ? "CRM intelligent & prospection automatisée pour agents immobiliers"
                : "Votre prospection immobilière en pilote automatique"}
            </p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {isSignup && (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Prénom</Label>
                      <Input
                        placeholder="Jean"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        className="bg-muted border-border mt-1"
                        required
                        autoComplete="given-name"
                        autoCapitalize="words"
                        enterKeyHint="next"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Nom</Label>
                      <Input
                        placeholder="Dupont"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        className="bg-muted border-border mt-1"
                        required
                        autoComplete="family-name"
                        autoCapitalize="words"
                        enterKeyHint="next"
                      />
                    </div>
                  </div>
                </>
              )}
              <div>
                <Label className="text-xs">Email</Label>
                <Input
                  type="email"
                  placeholder="agent@exemple.fr"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="bg-muted border-border mt-1"
                  required
                  inputMode="email"
                  autoComplete="email"
                  autoCapitalize="none"
                  spellCheck={false}
                  enterKeyHint="next"
                />
              </div>
              <div>
                <Label className="text-xs">Mot de passe</Label>
                <div className="relative mt-1">
                  <Input
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="bg-muted border-border pr-10"
                    required
                    minLength={6}
                    autoComplete={isSignup ? "new-password" : "current-password"}
                    enterKeyHint={isSignup ? "next" : "go"}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              {!isSignup && (
                <div className="flex justify-end -mt-2">
                  <Link
                    to="/forgot-password"
                    className="text-xs text-muted-foreground hover:text-primary"
                  >
                    Mot de passe oublié ?
                  </Link>
                </div>
              )}
              {isSignup && (
                <div>
                  <Label className="text-xs flex items-center gap-1">
                    <MapPin className="h-3 w-3" />
                    Ville
                  </Label>
                  <Input
                    placeholder="Paris"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    className="bg-muted border-border mt-1"
                    autoComplete="address-level2"
                    autoCapitalize="words"
                    enterKeyHint="go"
                  />
                </div>
              )}
              <Button type="submit" variant="cta" className="w-full" disabled={loading}>
                {loading ? "Chargement..." : isSignup ? "Créer mon compte" : "Se connecter"}
              </Button>
            </form>
            <div className="relative my-4">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-border" />
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="bg-card px-2 text-muted-foreground">ou</span>
              </div>
            </div>

            <Button
              type="button"
              variant="outline"
              className="w-full flex items-center gap-2 min-h-[44px]"
              onClick={() => handleOAuth("google")}
            >
              <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              Continuer avec Google
            </Button>

            <Button
              type="button"
              variant="outline"
              className="w-full flex items-center gap-2 min-h-[44px]"
              onClick={() => handleOAuth("linkedin_oidc")}
            >
              <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true" fill="#0A66C2">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
              </svg>
              Continuer avec LinkedIn
            </Button>

            <p className="text-xs text-center text-muted-foreground mt-4">
              {isSignup ? "Déjà un compte ?" : "Pas encore de compte ?"}{" "}
              <button
                onClick={() => setIsSignup(!isSignup)}
                className="text-primary hover:underline"
              >
                {isSignup ? "Se connecter" : "S'inscrire"}
              </button>
            </p>
          </CardContent>
        </Card>

        <p className="text-center text-3xs text-muted-foreground">
          Besoin d'aide ?{" "}
          <a href="mailto:contact@systeme1-immo.fr" className="hover:text-foreground underline-offset-2 hover:underline">contact@systeme1-immo.fr</a>
        </p>
      </div>
    </div>
  );
};

export default Login;
